import { useLanguage } from '@/hooks/useLanguage'
import type { Lang } from '@/types/lang'

interface Props {
  sourceLang?: Lang
  /** Языки ПЕРЕВОДОВ, показанных в каталоге (без языка оригинала) — считает
   *  `publishedTranslationLangs`. Своей копии правила здесь нет: у черновика
   *  `publishedLangs` курса пуст всегда, и бейдж по нему врал бы. */
  publishedLangs?: Lang[]
  /** Язык, перевод которого залит, а строки нет. Зеркало серверного правила
   *  (`unpublishedFilledLang`): работа автора готова, но курса на этом языке
   *  в каталоге не будет, пока язык не объявлен. */
  unpublishedFilledLang?: Lang
}

// Пилюля на поверхности карточки (не на обложке), поэтому цвета — токены темы
const CHIP =
  'rounded-md px-[0.6em] py-[0.2em] text-[clamp(0.625rem,3.4cqw,0.8125rem)] font-semibold uppercase tracking-wide'
const SOLID = `${CHIP} bg-foreground/10 text-foreground/80`
// Язык готов, но не объявлен: пунктир цвета warning — «работа есть, в каталоге нет»
const MUTED = `${CHIP} border border-dashed border-warning/70 text-warning`

/** Сколько языков показываем пилюлями; остальные сворачиваются в «+N» с подсказкой. */
const MAX_VISIBLE = 2

/** Языки курса на карточке «Мои курсы»: язык оригинала первым, затем показанные
 *  переводы, последним — готовый, но не объявленный язык, приглушённым бейджем.
 *  Языков может быть много: после `MAX_VISIBLE` остаток уходит в «+N», а полный
 *  список лежит в подсказке. Только чтение — объявляет язык переключатель во
 *  вкладке «Языки». */
export const CourseLangsBadge = ({ sourceLang, publishedLangs, unpublishedFilledLang }: Props) => {
  const { t } = useLanguage()
  const langs = [...new Set([...(sourceLang ? [sourceLang] : []), ...(publishedLangs ?? [])])]
  // Только языки, которые у курса есть (оригинал и опубликованные переводы). Залитый,
  // но не объявленный язык на карточке не показываем — его объясняет вкладка «Язык».
  void unpublishedFilledLang
  const items = langs.map((lang) => ({ lang, muted: false }))
  if (items.length === 0) return null

  const visible = items.slice(0, MAX_VISIBLE)
  const hidden = items.slice(MAX_VISIBLE)

  return (
    <div className="ml-auto flex items-center gap-1.5">
      {visible.map(({ lang, muted }) => (
        <span
          key={lang}
          className={muted ? MUTED : SOLID}
          // Своя подпись только у неё: у остальных пилюль подсказки нет
          title={muted ? t('langUndeclared') : undefined}
        >
          {lang.toUpperCase()}
        </span>
      ))}
      {hidden.length > 0 ? (
        <span
          className={`${CHIP} bg-foreground/5 text-muted-foreground`}
          title={hidden.map(({ lang }) => lang.toUpperCase()).join(', ')}
        >
          +{hidden.length}
        </span>
      ) : null}
    </div>
  )
}
